import { Component, computed, inject, LOCALE_ID, signal } from '@angular/core';
import { arabicNameValidators, optionalName } from '../../../../validators/name-rules';
import { SubcategoryService } from '../../../../services/subcategory/subcategory.service';
import { SubcategoryFileMgt } from '../../../../services/subcategory/fileMgt.subcategory.service';
import { Router } from '@angular/router';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  SubcategoriesList,
  SubcategoriesListRequest,
} from '../../../../pages/admin-dashboard/admin-subcategories/admin-subcategories.model';
import { CategoryService } from '../../../../services/category/category.service';
import { CategoryVTO } from '../../../../pages/admin-dashboard/categories/category.model';

@Component({
  selector: 'app-add-subcategory-form',
  imports: [ReactiveFormsModule],
  templateUrl: './add-subcategory-form.html',
  styleUrl: './add-subcategory-form.scss',
})
export class AddSubcategoryForm {
  private subcategoryService = inject(SubcategoryService);
  private subcategoryFileMgt = inject(SubcategoryFileMgt);
  private categoryService = inject(CategoryService);
  private router = inject(Router);
  private locale = inject(LOCALE_ID);
  isArabic = computed(() => this.locale.startsWith('ar'));
  categories: CategoryVTO[] = [];

  loading = signal<boolean>(false);
  errorCreatingSubcategory = signal<string | null>(null);

  selectedImage: File | null = null;
  imagePreview: string | null = null;
  formSubmitted = false;
  isDragging = false;

  subcategoryForm = new FormGroup({
    titleEn: new FormControl('', [Validators.minLength(2), Validators.maxLength(100)]),
    titleAr: new FormControl('', arabicNameValidators(100)),
    descriptionEn: new FormControl('', [Validators.maxLength(4000)]),
    descriptionAr: new FormControl('', [Validators.maxLength(4000)]),
    categoryId: new FormControl<number | null>(null, [Validators.required]),
    profileImageFid: new FormControl(''),
  });

  ngOnInit() {
    this.categoryService.getAllCategories({ orderBy: 'CREATION_DATE' }).subscribe({
      next: (res) => {
        // Categories loaded
        this.categories = res.list;
      },
      error: () => {},
    });
  }

  onFileSelect(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];
    this.processFile(file);

    input.value = '';
  }

  processFile(file: File) {
    if (!file.type.match(/image\/(png|jpe?g)/)) {
      alert($localize`:@@categoryFormFileTypeError:Only PNG and JPG files are allowed`);
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert($localize`:@@categoryFormFileSizeError:File ${file.name} exceeds 5MB limit`);
      return;
    }

    this.selectedImage = file;

    const reader = new FileReader();
    reader.onload = (e) => {
      this.imagePreview = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.processFile(files[0]);
    }
  }

  removeImage() {
    this.selectedImage = null;
    this.imagePreview = null;
  }

  onSubmit() {
    this.formSubmitted = true;

    if (!this.selectedImage) {
      this.errorCreatingSubcategory.set('Please upload a subcategory logo');
      return;
    }

    if (this.subcategoryForm.invalid) {
      for (const key in this.subcategoryForm.controls) {
        this.subcategoryForm.get(key)?.markAsTouched();
      }
      return;
    }

    this.loading.set(true);
    this.errorCreatingSubcategory.set(null);

    this.subcategoryFileMgt.uploadImage(this.selectedImage).subscribe({
      next: (result) => {
        const formValue = this.subcategoryForm.getRawValue();
        const payload: SubcategoriesListRequest = {
          titleEn: optionalName(formValue.titleEn),
          titleAr: formValue.titleAr!.trim(),
          descriptionEn: formValue.descriptionEn!,
          descriptionAr: formValue.descriptionAr!,
          categoryId: formValue.categoryId!,
          isActive: true,
          profileImageFid: result.fid,
        };

        this.subcategoryService.createSubcategory(payload).subscribe({
          next: () => {
            this.loading.set(false);
            this.router.navigate(['/admin/subcategories']);
          },
          error: (err: Error) => {
            console.error('Error creating category:', err);
            this.loading.set(false);
            this.errorCreatingSubcategory.set('Failed to create category. Please try again.');
          },
        });
      },
      error: (error) => {
        console.error('Error uploading image:', error);
        this.loading.set(false);
        this.errorCreatingSubcategory.set('Failed to upload image. Please try again.');
      },
    });
  }

  onCancel() {
    this.router.navigate(['/admin/subcategories']);
  }
}
